import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getEsgCodeDescriptions,
  getEsgFundData,
  getEsgOverviewMonitor,
  getEsgStressTest,
  getFundEsgTimeSeriesData,
  getIndicatorCompliance,
  getIndicatorCoverageRatio,
  type EsgPageId,
} from '../esg.routes';
import type {
  EsgCodeDescriptions,
  EsgOverviewPageModel,
  EsgPortfolioSummaryPageModel,
  EsgStressTestPageModel,
  FundEsgTimeSeriesApiResponse,
  IndicatorCompliancePageModel,
  IndicatorCoverageRatioPageModel,
} from '../types';

export const useEsgStressTestQuery = (
  fundName?: string,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<EsgStressTestPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<EsgStressTestPageModel, Error>({
    queryKey: ['manco-esg-stress-test', fundName, positionDate],
    queryFn: () => getEsgStressTest(fundName!, positionDate!),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled:
      !!fundName &&
      fundName !== 'none_selected' &&
      !!positionDate &&
      (options?.enabled ?? true),
  });
};

export const useEsgFundData = (
  fundName: string | undefined,
  positionDate: string | undefined,
  pageName: EsgPageId,
  options?: Omit<
    UseQueryOptions<EsgPortfolioSummaryPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<EsgPortfolioSummaryPageModel, Error>({
    queryKey: ['esg-fund-data', fundName, positionDate, pageName],
    queryFn: () => getEsgFundData(fundName!, positionDate!, pageName),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled:
      !!fundName &&
      fundName !== 'none_selected' &&
      !!positionDate &&
      (options?.enabled ?? true),
  });
};

export const useEsgCodeDescriptions = (
  options?: Omit<
    UseQueryOptions<EsgCodeDescriptions, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<EsgCodeDescriptions, Error>({
    queryKey: ['esg-code-descriptions'],
    queryFn: () => getEsgCodeDescriptions(),
    staleTime: 60 * 60 * 1000,
    ...options,
  });
};

export const useFundEsgTimeSeriesData = (
  fundId: string | undefined,
  positionDate: string | undefined,
  options?: Omit<
    UseQueryOptions<FundEsgTimeSeriesApiResponse, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<FundEsgTimeSeriesApiResponse, Error>({
    queryKey: ['fund-esg-timeseries', fundId, positionDate],
    queryFn: () => getFundEsgTimeSeriesData(fundId!, positionDate!),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: !!fundId && !!positionDate && (options?.enabled ?? true),
  });
};

export const esgOverviewMonitorQueryKey = (
  positionDate?: string,
  raptorPlatformId?: string,
) => ['esg-overview-monitor', positionDate, raptorPlatformId] as const;

export interface UseEsgOverviewMonitorQueryParams {
  positionDate?: string;
  raptorPlatformId?: string;
}

export const useEsgOverviewMonitorQuery = (
  params: UseEsgOverviewMonitorQueryParams,
  options?: Omit<
    UseQueryOptions<EsgOverviewPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<EsgOverviewPageModel, Error>({
    queryKey: esgOverviewMonitorQueryKey(
      params.positionDate,
      params.raptorPlatformId,
    ),
    queryFn: () =>
      getEsgOverviewMonitor(params.positionDate, params.raptorPlatformId),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: !!params.positionDate && (options?.enabled ?? true),
  });
};

export const indicatorCoverageRatioQueryKey = (
  fundId?: string,
  positionDate?: string,
) => ['indicator-coverage-ratio-monitor', fundId, positionDate] as const;

export const useIndicatorCoverageRatioQuery = (
  fundId?: string,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<IndicatorCoverageRatioPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<IndicatorCoverageRatioPageModel, Error>({
    queryKey: indicatorCoverageRatioQueryKey(fundId, positionDate),
    queryFn: () => getIndicatorCoverageRatio(fundId!, positionDate!),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled:
      !!fundId &&
      fundId !== 'none_selected' &&
      !!positionDate &&
      (options?.enabled ?? true),
  });
};

export const indicatorComplianceQueryKey = (
  fundId?: string,
  positionDate?: string,
) => ['indicator-compliance', fundId, positionDate] as const;

export const useIndicatorComplianceQuery = (
  fundId?: string,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<IndicatorCompliancePageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<IndicatorCompliancePageModel, Error>({
    queryKey: indicatorComplianceQueryKey(fundId, positionDate),
    queryFn: () => getIndicatorCompliance(fundId!, positionDate!),
    staleTime: 0,
    ...options,
    enabled:
      !!fundId &&
      fundId !== 'none_selected' &&
      !!positionDate &&
      (options?.enabled ?? true),
  });
};
